/**
 * ProductCardEditor — WYSIWYG sub-element editor used inside the Visual
 * Builder RIGHT settings pane when the `product_card` component is
 * selected.
 *
 * Layout:
 *   [ Live Sample tile ] [ Accordion of sub-element panels ]
 *
 * The editor edits the top-level `card` blob of the current /ui-
 * customization document. This is the same blob every current consumer
 * (ProductCard.jsx web + ProductCard.tsx native) already reads, so
 * changes go live immediately with zero migration.
 *
 * Per-section variants (Phase 7 extension):
 *   If a section (e.g. "Best Sellers", "Flash Deals") wants its OWN card
 *   variant (different ADD button, different discount tag), that section
 *   stores its overrides at `components.<section_id>.card_override`.
 *   The consumer merges: base card <- section.card_override. This is
 *   additive and optional; sections without an override use the base
 *   card as today.
 */
import { useMemo, useState } from "react";
import * as LucideIcons from "lucide-react";
import {
  Sparkles, Settings2, X, ChevronDown, ChevronRight, Eye, EyeOff, RotateCcw,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PRODUCT_CARD_SCHEMA, PRODUCT_CARD_DEFAULTS, productCardFieldKeys } from "@/lib/visualBuilder/productCardSchema";
import ProductCardSample from "./ProductCardSample";

const CARD_KEYS = productCardFieldKeys();

export default function ProductCardEditor({ card, onChange, onClose, onReset }) {
  const [openSection, setOpenSection] = useState("card_shell");
  const [query, setQuery] = useState("");

  const merged = useMemo(() => ({ ...PRODUCT_CARD_DEFAULTS, ...(card || {}) }), [card]);

  const filteredSchema = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return PRODUCT_CARD_SCHEMA;
    return PRODUCT_CARD_SCHEMA
      .map((sec) => ({
        ...sec,
        fields: sec.fields.filter(
          (f) => f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q),
        ),
      }))
      .filter((sec) => sec.fields.length > 0);
  }, [query]);

  const setField = (key, value) => {
    if (!CARD_KEYS.has(key)) return; // safety — never write unknown keys
    onChange({ [key]: value });
  };

  const setVisibility = (visKey, on) => {
    if (!visKey) return;
    onChange({ [visKey]: on });
  };

  return (
    <div className="p-4 space-y-4" data-testid="vb-product-card-editor">
      {/* Header */}
      <div className="flex items-start gap-2">
        <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Product Card · WYSIWYG</div>
          <div className="text-[15px] font-semibold text-slate-900 truncate">Card Sub-elements</div>
          <div className="text-[10.5px] text-slate-400">Edit every part of the card — image, price, tags, buttons.</div>
        </div>
        <button type="button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100" data-testid="vb-pc-close">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Live sample */}
      <div className="bg-slate-100 rounded-xl p-3 flex flex-col items-center gap-2" data-testid="vb-pc-preview">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Live Sample</div>
        <ProductCardSample card={merged} />
        <div className="flex items-center gap-2 mt-1">
          <button
            type="button"
            onClick={onReset}
            className="text-[11px] text-slate-500 hover:text-red-600 inline-flex items-center gap-1"
            data-testid="vb-pc-reset"
          >
            <RotateCcw className="w-3 h-3" /> Reset to defaults
          </button>
        </div>
      </div>

      {/* Search */}
      <div>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search field (color, radius, MRP…)"
          className="h-9 text-[12px]"
          data-testid="vb-pc-search"
        />
      </div>

      {/* Sub-element accordion */}
      <div className="space-y-2">
        {filteredSchema.map((sec) => {
          const Icon = LucideIcons[sec.icon] || Settings2;
          const isOpen = openSection === sec.id || !!query;
          const visKey = sec.visibility_key;
          const isVisible = visKey ? merged[visKey] !== false : true;
          return (
            <div key={sec.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`vb-pc-sec-${sec.id}`}>
              <button
                type="button"
                onClick={() => setOpenSection(isOpen ? null : sec.id)}
                className={`w-full flex items-center gap-2 px-3 py-2.5 text-left ${isOpen ? "bg-[#FFF3F4]" : "hover:bg-slate-50"}`}
                data-testid={`vb-pc-sec-toggle-${sec.id}`}
              >
                <div className={`w-7 h-7 rounded-md grid place-items-center shrink-0 ${isOpen ? "bg-[#E32636] text-white" : "bg-slate-100 text-slate-600"}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="text-[12.5px] font-semibold text-slate-900 flex-1 truncate">{sec.title}</div>
                {visKey && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setVisibility(visKey, !isVisible); }}
                    className={`w-6 h-6 grid place-items-center rounded text-slate-500 hover:bg-slate-100 ${isVisible ? "" : "opacity-50"}`}
                    title={isVisible ? "Hide this element" : "Show this element"}
                    data-testid={`vb-pc-sec-vis-${sec.id}`}
                  >
                    {isVisible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5 text-red-500" />}
                  </button>
                )}
                {isOpen
                  ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                  : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
              </button>

              {isOpen && (
                <div className="p-3 grid grid-cols-2 gap-3 border-t border-slate-100">
                  {sec.fields.map((f) => (
                    <FieldRow
                      key={f.key}
                      field={f}
                      value={merged[f.key]}
                      defaultValue={PRODUCT_CARD_DEFAULTS[f.key]}
                      onChange={(v) => setField(f.key, v)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {filteredSchema.length === 0 && (
          <div className="text-center text-[12px] text-slate-400 py-6">No fields match "{query}"</div>
        )}
      </div>

      <div className="pt-2 text-[10.5px] text-slate-400 border-t border-slate-100 leading-relaxed">
        Business logic (prices, discounts, stock) is never editable here — only visual presentation.
      </div>
    </div>
  );
}

/* ─── Field row ─────────────────────────────────────────────────────── */

function FieldRow({ field, value, defaultValue, onChange }) {
  const testId = `vb-pc-field-${field.key}`;
  const wide = field.type === "text" || field.type === "font" || field.hint;

  return (
    <div className={wide ? "col-span-2" : "col-span-1"}>
      <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{field.label}</Label>
      {field.type === "text" && (
        <Input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={String(defaultValue ?? "")}
          className="h-8 text-[12px]"
          data-testid={testId}
        />
      )}
      {field.type === "number" && (
        <Input
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
          placeholder={String(defaultValue ?? "")}
          min={field.min} max={field.max} step={field.step || 1}
          className="h-8 text-[12px]"
          data-testid={testId}
        />
      )}
      {field.type === "color" && (
        <div className="flex items-center gap-1.5">
          <input
            type="color"
            value={value || "#ffffff"}
            onChange={(e) => onChange(e.target.value)}
            className="h-8 w-8 rounded-lg border border-slate-200 cursor-pointer"
            data-testid={`${testId}-picker`}
          />
          <Input
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="auto"
            className="h-8 text-[11px] font-mono flex-1"
            data-testid={testId}
          />
        </div>
      )}
      {field.type === "select" && (
        <select
          value={value ?? defaultValue ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            // Coerce numeric option values back to numbers.
            const opt = field.options.find((o) => String(o.value) === v);
            onChange(opt ? opt.value : v);
          }}
          className="h-8 w-full text-[12px] rounded-md border border-slate-200 px-2 bg-white"
          data-testid={testId}
        >
          {field.options.map((o) => (
            <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
          ))}
        </select>
      )}
      {field.type === "toggle" && (
        <div className="flex items-center gap-2 h-8">
          <Switch
            checked={value !== false && value !== undefined ? !!value : !!defaultValue}
            onCheckedChange={(v) => onChange(v)}
            data-testid={testId}
          />
          <span className="text-[11px] text-slate-500">{value !== false && (value ?? defaultValue) ? "On" : "Off"}</span>
        </div>
      )}
      {field.hint && (
        <div className="text-[10px] text-slate-400 mt-0.5">{field.hint}</div>
      )}
    </div>
  );
}
